import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, MapPinIcon, CheckCircleIcon, PenToolIcon, CalendarIcon, HeartIcon, GlobeIcon, StoreIcon, BookIcon } from 'lucide-react';
import { Bookstore } from '../../data/bookstoresData';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import SaveToFavorites from '../UI/SaveToFavorites';
interface BookstoreCardProps {
  bookstore: Bookstore;
  viewMode: 'grid' | 'list';
}
// Solid badge colors for the card (like genre badges on UnifiedBookCard)
const getSolidTypeColor = (type: string): string => {
  switch (type) {
    case 'brick-and-mortar':
      return 'bg-emerald-600';
    case 'online-only':
      return 'bg-blue-600';
    case 'hybrid':
      return 'bg-purple-600';
    case 'pop-up':
      return 'bg-amber-600';
    case 'specialty':
      return 'bg-rose-600';
    default:
      return 'bg-gray-600';
  }
};
// Semi-transparent overlay badge colors
const getOverlayTypeColor = (type: string): string => {
  switch (type) {
    case 'brick-and-mortar':
      return 'bg-emerald-500/80';
    case 'online-only':
      return 'bg-blue-500/80';
    case 'hybrid':
      return 'bg-purple-500/80';
    case 'pop-up':
      return 'bg-amber-500/80';
    case 'specialty':
      return 'bg-rose-500/80';
    default:
      return 'bg-gray-500/80';
  }
};
const formatType = (type: string) => {
  return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};
export const BookstoreCard: React.FC<BookstoreCardProps> = ({
  bookstore,
  viewMode
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const [imgError, setImgError] = useState(false);
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'brick-and-mortar':
        return dk.isDark ? 'bg-emerald-900/40 text-emerald-300 border-emerald-800' : 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'online-only':
        return dk.isDark ? 'bg-blue-900/40 text-blue-300 border-blue-800' : 'bg-blue-100 text-blue-800 border-blue-200';
      case 'hybrid':
        return dk.isDark ? 'bg-purple-900/40 text-purple-300 border-purple-800' : 'bg-purple-100 text-purple-800 border-purple-200';
      case 'pop-up':
        return dk.isDark ? 'bg-amber-900/40 text-amber-300 border-amber-800' : 'bg-amber-100 text-amber-800 border-amber-200';
      case 'specialty':
        return dk.isDark ? 'bg-rose-900/40 text-rose-300 border-rose-800' : 'bg-rose-100 text-rose-800 border-rose-200';
      default:
        return dk.isDark ? 'bg-gray-800 text-gray-300 border-gray-700' : 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getBadgeStyle = () => {
    return dk.isDark ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200';
  };
  const verifiedColor = isAfterTide ? 'text-[#e2b04a]' : 'text-blue-500';
  const buttonColor = isAfterTide ? 'bg-[#e2b04a] text-[#1a1a2e] hover:bg-[#d1a03a]' : 'bg-indigo-600 text-white hover:bg-indigo-700';
  // List view (unchanged)
  if (viewMode === 'list') {
    return <div className={`group relative flex items-center p-4 rounded-xl border transition-all duration-200 ${dk.isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm hover:shadow'}`} data-id="element-1114" data-mp="36swmem6we5tt">
        <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden mr-4 border border-gray-200 dark:border-gray-700" data-id="element-1115" data-mp="3gsndsf05w4po">
          <img src={bookstore.logo} alt={bookstore.name} className="w-full h-full object-cover" data-id="element-1116" data-mp="vz0i5n0cs3g9" />
        </div>

        <div className="flex-1 min-w-0 mr-4" data-id="element-1117" data-mp="yki0spjyam2p">
          <div className="flex items-center gap-2 mb-1" data-id="element-1118" data-mp="yl9kb209wtcm">
            <h3 className={`font-bold text-base sm:text-lg truncate ${dk.textPrimary}`} data-id="element-1119" data-mp="tlewx8fpilwb">
              {bookstore.name}
            </h3>
            {bookstore.verified && <CheckCircleIcon className={`h-4 w-4 flex-shrink-0 ${verifiedColor}`} data-id="element-1120" data-mp="13jum35v6bl36" />}
            <span className={`text-[10px] px-2 py-0.5 ${rt.badgeRadius} font-medium border ${getTypeColor(bookstore.type)} hidden sm:inline-block`} data-id="element-1121" data-mp="1hiarmthav5jq">
              {formatType(bookstore.type)}
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm mb-1" data-id="element-1122" data-mp="1rb03vqkocng4">
            <div className={`flex items-center ${dk.textSecondary}`} data-id="element-1123" data-mp="1mako6scx73uz">
              <MapPinIcon className="h-3.5 w-3.5 mr-1" data-id="element-1124" data-mp="2092bioywyecd" />
              <span className="truncate" data-id="element-1125" data-mp="207hcon5kg98p">
                {bookstore.city ? `${bookstore.city}, ` : ''}
                {bookstore.country}
              </span>
            </div>
            <div className="flex items-center text-yellow-500" data-id="element-1126" data-mp="2exbzpzsstg8p">
              <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-1127" data-mp="2juklead5o2ep" />
              <span className="font-bold" data-id="element-1128" data-mp="25vulkmyybomn">{bookstore.rating}</span>
              <span className={`ml-1 text-xs ${dk.textMuted}`} data-id="element-1129" data-mp="25vkyp897dxl6">
                ({bookstore.reviewCount})
              </span>
            </div>
          </div>

          {bookstore.address && <p className={`text-xs mb-2 truncate ${dk.textMuted}`} data-id="element-1130" data-mp="1w4qba0k80ry5">
              {bookstore.address}
            </p>}

          <div className="hidden sm:flex flex-wrap gap-1.5" data-id="element-1131" data-mp="1n5d093gg7vbb">
            {bookstore.specializations.slice(0, 3).map((spec, idx) => <span key={idx} className={`text-[10px] px-2 py-0.5 rounded-md ${getBadgeStyle()}`} data-id="element-1132" data-mp="1r7zvlecbvyd9" data-mpi={idx}>
                {spec}
              </span>)}
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col items-end gap-2" data-id="element-1133" data-mp="1i5i52npn4jkg">
          <Link to={`/bookstores/${bookstore.slug}`} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm ${buttonColor}`} data-id="element-1134" data-mp="3jwh8gxybwdut">
            View Store
          </Link>
          <div className={`flex items-center text-xs ${dk.textMuted}`} data-id="element-1135" data-mp="3b3dos7x3b4rq">
            <BookIcon className="h-3 w-3 mr-1" data-id="element-1136" data-mp="365c79rfbbdid" />
            {bookstore.bookCount} books
          </div>
        </div>
      </div>;
  }
  // Grid View — matches UnifiedBookCard pattern exactly
  return <div className="relative group w-full" data-id="element-1137" data-mp="2on8wspvva3s">
      <Link to={`/bookstores/${bookstore.slug}`} className="block relative aspect-square rounded-lg overflow-hidden shadow-md transition-transform duration-200 group-hover:scale-105" data-id="element-1138" data-mp="3ttn1ibqi6exo">
        {/* Store image */}
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-1139" data-mp="2ig1fgzfxwtgd">
            <StoreIcon className="h-10 w-10 text-indigo-400 mb-2" data-id="element-1140" data-mp="23bouu4lxvgvy" />
            <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-2" data-id="element-1141" data-mp="239c3j0uaju68">
              {bookstore.name}
            </span>
          </div> : <img src={bookstore.bannerImage || bookstore.logo} alt={bookstore.name} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-1142" data-mp="2d8unhha7bfya" />}

        {/* Type badge — top left, solid color (like genre badge) */}
        <span className={`absolute top-2 left-2 text-white text-xs ${getSolidTypeColor(bookstore.type)} px-2 py-0.5 ${rt.badgeRadius} z-10`} data-id="element-1143" data-mp="2r341uaonumdj">
          {formatType(bookstore.type)}
        </span>

        {/* Verified badge — bottom left (like content warning icon position) */}
        {bookstore.verified && <span className="absolute bottom-2 left-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white shadow-md z-10" data-id="element-1144" data-mp="16kwbmy3pl8py">
            <CheckCircleIcon className="h-3.5 w-3.5" data-id="element-1145" data-mp="38h55axrjipsm" />
          </span>}

        {/* Hover Overlay — pure CSS group-hover, matches UnifiedBookCard */}
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col p-3 transition-opacity duration-200 text-white z-20 opacity-0 group-hover:opacity-100" data-id="element-1146" data-mp="3i91aess1ng6c">
          {/* Top section */}
          <div className="min-w-0" data-id="element-1147" data-mp="2pj2eqejcgksp">
            {/* Name + verified */}
            <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1148" data-mp="2kkr6g88p6icl">
              <h3 className="font-bold text-white text-sm line-clamp-1" data-id="element-1149" data-mp="2kmlqzlg248g5">
                {bookstore.name}
              </h3>
              {bookstore.verified && <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-400" data-id="element-1150" data-mp="2uiozra5ez5c2" />}
            </div>

            {/* Type badge in overlay */}
            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium mb-1.5 text-white ${getOverlayTypeColor(bookstore.type)}`} data-id="element-1151" data-mp="9spytkmsdwiy">
              {formatType(bookstore.type)}
            </span>

            {/* Location — city + country only, NO address */}
            <div className="text-[11px] text-gray-300 flex items-center mb-1.5" data-id="element-1152" data-mp="7e2o3wkdnpxr">
              <MapPinIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-1153" data-mp="7g5j6v0e099c" />
              <span className="line-clamp-1" data-id="element-1154" data-mp="cde77c87v7ii">
                {bookstore.city ? `${bookstore.city}, ` : ''}
                {bookstore.country}
              </span>
            </div>

            {/* Specialization tags */}
            <div className="mb-1.5 flex flex-wrap gap-1" data-id="element-1155" data-mp="153nsq81vhfee">
              {bookstore.specializations.slice(0, 4).map((spec, idx) => <span key={idx} className="px-1.5 py-0.5 bg-indigo-500/80 text-white rounded text-[10px]" data-id="element-1156" data-mp="vb8cbf684yvm" data-mpi={idx}>
                  {spec}
                </span>)}
              {bookstore.specializations.length > 4 && <span className="px-1.5 py-0.5 bg-white/15 text-gray-300 rounded text-[10px]" data-id="element-1157" data-mp="21jxf8g2nblx1">
                  +{bookstore.specializations.length - 4}
                </span>}
            </div>

            {/* Shipping only (from features) */}
            {bookstore.features.internationalShipping && <div className="text-[11px] text-gray-300 flex items-center mb-1.5" data-id="element-1158" data-mp="1wjrsneb8gplt">
                <GlobeIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-1159" data-mp="1hv53kkvt0jhe" />
                <span data-id="element-1160" data-mp="1mmdczgx4w4uk">Ships Internationally</span>
              </div>}
          </div>

          {/* Bottom section — rating left, book count right */}
          <div className="mt-auto flex items-center justify-between pt-1" data-id="element-1161" data-mp="18wtfapietxs4">
            <div className="flex items-center gap-1" data-id="element-1162" data-mp="1dv4meshblu6n">
              <StarIcon className="h-4 w-4 text-amber-400" data-id="element-1163" data-mp="ywsqwp5oorh2" />
              <span className="text-[11px] font-medium" data-id="element-1164" data-mp="yxcasbew9pxq">
                {bookstore.rating}
              </span>
              {bookstore.reviewCount > 0 && <span className="text-gray-400 text-[11px]" data-id="element-1165" data-mp="k9kexm4jwi41">
                  ({bookstore.reviewCount})
                </span>}
            </div>
            <div className="flex items-center gap-1.5" data-id="element-1166" data-mp="gal7nuvck99f">
              <div className="bg-white/15 rounded-full p-1" title="Books available" data-id="element-1167" data-mp="u9b4rai628xs">
                <BookIcon className="h-3 w-3 text-white" data-id="element-1168" data-mp="pb9pl0nv2trl" />
              </div>
              <span className="text-[11px] text-gray-300" data-id="element-1169" data-mp="137etefog7ltt">
                {bookstore.bookCount}
              </span>
            </div>
          </div>
        </div>
      </Link>

      {/* Save heart — top right, outside the Link (like book cards) */}
      <div className="absolute top-2 right-2 z-30" data-id="element-1170" data-mp="17zy6lbj84ey3">
        <SaveToFavorites itemId={bookstore.id} size="sm" data-id="element-1171" data-mp="1807wkz49qm3s" />
      </div>
    </div>;
};